import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import LabelButton from "../../components/reusable-components/buttons/LabelButton";
import InnoTextInput from "../../components/inputs/InnoTextInput";
import { useValidation } from "../../helpers/validationHelpers";
import TextBox from "../../components/inputs/TextBox";
import GroupAutoComplete from "../../components/reusable-components/GroupAutoComplete";
import groupCPSSchools from "../../contexts/groupCPSSchools";
import signup from "../../apiCalls/signup";
import { useBannerDispatch } from "../../contexts/BannerContext";
import { useAuthDispatch } from "../../contexts/AuthContext";
import { camelize } from "../../helpers/stringHelpers";

export default function CustomRegisterForm({
  user,
  inviteToken,
}) {
  const navigate = useNavigate();
  const authDispatch = useAuthDispatch();
  const [formInputs, setFormInputs] = useState({
    jobTitle: user?.jobTitle || "",
    schoolName: user?.Accounts?.[0]?.name || user?.accountName || "",
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pswdErrorMsg, setPswdEroorMsg] = useState("");
  const [pswdHelpText, setPswdHelpText] = useState("");
  const [submitInProcess, setSubmitInProcess] = useState(false);
  const bannerDispatch = useBannerDispatch();
  const { errors, validate } = useValidation({
    jobTitle: { isNotBlank: true },
    schoolName: { isNotBlank: true },
    email: { isValidEmail: true },
    password: {
      is8OrMoreCharacters: true,
      hasCapitalLetter: true,
      hasLowercaseLetter: true,
      hasNumber: true,
      hasSymbol: true,
    },
  });

  //update the pswdHelpText
  useEffect(() => {
    if (password && !errors?.password?.isValid) {
      const pswdHelpMessageList = [
        { hasCapitalLetter: "Must contain at least one capital letter." },
        { hasLowercaseLetter: "Must contain at least one lowercase letter." },
        { hasSymbol: "Must contain at least one symbol." },
        { is8OrMoreCharacters: "Must be at least 8 characters." },
        { hasNumber: "Must contain at least one number." },
      ];
      const errorsList = Object.keys(errors.password?.errorMessages || {});

      const formatedText = (
        <List sx={{ paddingY: "1px" }}>
          {pswdHelpMessageList.map((msg, index) => {
            if (errorsList.includes(Object.keys(msg)[0])) {
              return pswdHelpTextList(index, Object.values(msg)[0], true);
            } else {
              return pswdHelpTextList(index, Object.values(msg)[0], false);
            }
          })}
        </List>
      );
      setPswdHelpText(formatedText);
    } else {
      setPswdHelpText("");
    }
  }, [errors]);

  const handleGoogleRegister = async () => {
    let validForm = true;
    ["schoolName", "jobTitle"].forEach((input) => {
      validate(`${input}`, formInputs[[input]]);
      if (!errors[[input]].isValid) validForm = false;
    });
    if (!validForm) {
      return;
    }

    setSubmitInProcess(true);

    const response = await fetch("/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: user?.email,
        schoolName: formInputs.schoolName,
        schoolSlug: camelize(formInputs.schoolName, true).replace(
          /[^a-zA-Z0-9]/g,
          ""
        ),
        jobTitle: formInputs.jobTitle,
        inviteToken: user.invitationToken,
      }),
    });

    if (response.ok) {
      navigate("/", { replace: true });
      window.location.reload(); // This will refresh the main page after navigation
    } else {
      setSubmitInProcess(false);
      const data = await response.json();
      authDispatch({
        type: "setSelectedAccount",
        payload: data.account,
      });
      navigate("/", { replace: true });
    }
  };

  const handleEmailPswdRegister = async () => {
    // Reset previous password-specific error
    setPswdEroorMsg("");

    // Run all validations
    const { isValid: isJobTitleValid } = validate(
      "jobTitle",
      formInputs.jobTitle
    );
    const { isValid: isSchoolNameValid } = validate(
      "schoolName",
      formInputs.schoolName
    );
    const { isValid: isPasswordValid } = validate("password", password);

    // Check if passwords are empty
    if (!password || !confirmPassword) {
      setPswdEroorMsg("Password can't be empty");
      return;
    }

    // Check for password matching
    if (password !== confirmPassword) {
      setPswdEroorMsg("Passwords do not match");
      return;
    }

    // If any validation fails, stop
    if (!isJobTitleValid || !isSchoolNameValid || !isPasswordValid) {
      return;
    }

    setSubmitInProcess(true);

    const body = {
      schoolName: formInputs.schoolName,
      jobTitle: formInputs.jobTitle,
      email: user.email,
      password,
      token: inviteToken,
    };

    try {
      const res = await signup(body);
      if (!res.error) {
        // On successful registration, login the user directly
        navigate("/", { replace: true });
      } else {
        setSubmitInProcess(false);
        bannerDispatch({
          type: "changeBannerState",
          payload: {
            mainText: "We couldn’t complete your registration",
            subText: res.error || "An unknown error occurred.",
            tallBanner: true,
            success: false,
          },
        });
      }
    } catch (err) {
      setSubmitInProcess(false);
      bannerDispatch({
        type: "changeBannerState",
        payload: {
          mainText: "An unexpected error occurred",
          subText: "Please try again later.",
          tallBanner: true,
          success: false,
        },
      });
    }
  };

  const handleCompleteRegistration = async () => {
    if (user?.authInfo) {
      handleGoogleRegister();
    } else {
      handleEmailPswdRegister();
    }
  };

  const handleRoleInput = (val) =>
    setFormInputs({ ...formInputs, jobTitle: val });

  const handleSelectSchool = (e, val) => {
    if (!val) return;

    setFormInputs({
      ...formInputs,
      schoolName: val.headerName,
      schoolAdress: val.address,
    });
    validate("schoolName", val.headerName);
  };

  const handlePswdChange = (e, field) => {
    // for each field, set the value
    switch (field) {
      case "password":
        setPswdEroorMsg("");
        setPswdHelpText("");
        setPassword(e.target.value);
        break;
      case "confirmPassword":
        setPswdEroorMsg("");
        setPswdHelpText("");
        setConfirmPassword(e.target.value);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Grid
        item
        sx={{
          width: "100%",
          height: "auto",
          marginBottom: "20px",
        }}>
        <Grid
          container
          direction="column"
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "space-between",
            gap: "15px",
          }}>
          {/* school name */}
          <Grid
            item
            sx={{
              width: "100%",
              height: "67px",
            }}>
            <Typography
              sx={{
                color: "#A4A4A4",
                fontFamily: "Lato",
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                marginBottom: "10px",
              }}>
              School name
            </Typography>
            <Grid
              item
              sx={{
                width: "100%",
                height: "40px",
              }}>
              {(user?.invitationToken && !user?.invitationExpired) ||
              user.accountName ? (
                <Grid
                  container
                  sx={{
                    width: "100%",
                    height: "100%",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    paddingLeft: "14px",
                    borderRadius: "6px",
                    border: `1.5px solid ${
                      errors?.schoolName?.isValid
                        ? "var(--Neutrals-500, #E2E6E9)"
                        : "var(--Neutrals-500, #F5325B)"
                    }`,
                  }}>
                  <Typography
                    sx={{
                      width: "100%",
                      color: "var(--Neutrals-700, #C6CCD2)",
                      fontFamily: "Lato",
                      fontSize: 14,
                      fontStyle: "normal",
                      fontWeight: 700,
                      lineHeight: "normal",
                    }}>
                    {formInputs.schoolName}
                  </Typography>
                </Grid>
              ) : (
                <GroupAutoComplete
                  groupList={groupCPSSchools}
                  placeholder="Enter school name"
                  ascendingListByGroup={false}
                  handleSlection={handleSelectSchool}
                  showEndAdornment={false}
                  styles={{
                    height: "100%",
                    border: !errors?.schoolName?.isValid
                      ? "1px solid var(--error-500-primary, #F5325B)"
                      : "1.5px solid var(--Neutrals-500, #E2E6E9)",
                    placeholder: {
                      color: "var(--Neutrals-700, #C6CCD2)",
                    },
                  }}
                />
              )}
            </Grid>
            {/* error msg */}
            {!errors?.schoolName?.isValid && (
              <Typography
                sx={{
                  color: "#F5325B",
                  fontFamily: "Lato",
                  fontSize: 12,
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  paddingLeft: "14px",
                }}>
                {errors?.schoolName?.errorMessages?.isNotBlank}
              </Typography>
            )}
          </Grid>
          {/* email */}
          <Grid
            item
            sx={{
              width: "100%",
              height: "75px",
            }}>
            <Typography
              sx={{
                color: "#A4A4A4",
                fontFamily: "Lato",
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                marginBottom: "10px",
              }}>
              Email
            </Typography>
            <Grid
              item
              sx={{
                width: "100%",
                height: "40px",
              }}>
              <Grid
                container
                sx={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  paddingLeft: "14px",
                  borderRadius: "6px",
                  border: `1.5px solid ${
                    errors?.email?.isValid
                      ? "var(--Neutrals-500, #E2E6E9)"
                      : "var(--Neutrals-500, #F5325B)"
                  }`,
                }}>
                <Typography
                  sx={{
                    width: "100%",
                    color: "var(--Neutrals-700, #C6CCD2)",
                    fontFamily: "Lato",
                    fontSize: 14,
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "normal",
                  }}>
                  {user.email}
                </Typography>
              </Grid>
              {/* error msg */}
              {!errors?.email?.isValid && (
                <Typography
                  sx={{
                    color: "#F5325B",
                    fontFamily: "Lato",
                    fontSize: 12,
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "normal",
                    paddingLeft: "14px",
                  }}>
                  {errors?.email?.errorMessages?.isValidEmail}
                </Typography>
              )}
            </Grid>
          </Grid>
          {/* job title */}
          <Grid
            item
            sx={{
              width: "100%",
              height: "67px",
            }}>
            <Typography
              sx={{
                color: "#A4A4A4",
                fontFamily: "Lato",
                fontSize: 14,
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
                marginBottom: "10px",
              }}>
              Job Title
            </Typography>
            <Grid
              item
              sx={{
                width: "100%",
                height: "40px",
              }}>
              <InnoTextInput
                fieldName="jobTitle"
                placeholder="Please input your job title"
                fieldValue={formInputs.jobTitle}
                setValue={handleRoleInput}
                validate={validate}
                width="100%"
                height="40px"
                styledProps={{
                  fontFamily: "Lato",
                  fontSize: "14px",
                  fontWeight: 700,
                  fontStyle: "normal",
                  color: "var(--Primary-900, #0B0926)",
                }}
                placeholderStyles={{
                  fontFamily: "Lato",
                  fontSize: "14px",
                  fontWeight: 700,
                  fontStyle: "normal",
                  color: "var(--Neutrals-700, #C6CCD2)",
                }}
                errorIcon={false}
                showErrorIconWithError={false}
                errors={errors}
              />
            </Grid>
          </Grid>
          {/* password */}
          {!user?.authInfo && (
            <>
              <Grid
                item
                sx={{ width: "100%", height: "67px" }}
                className="password">
                <RegistrationFormLabel id="password-label">
                  Password
                </RegistrationFormLabel>
                <TextBox
                  labelId="password-label"
                  value={password}
                  handleChange={(e) => handlePswdChange(e, "password")}
                  fullWidth
                  type="password"
                  placeholder="***************"
                  customStyles={{
                    height: "40px",
                    border: "1.5px solid var(--Neutrals-500, #E2E6E9)",
                  }}
                />
              </Grid>
              <Grid
                item
                sx={{ width: "100%", height: "67px" }}
                className="confirm-password">
                <RegistrationFormLabel id="confirm-password-label">
                  Re-enter Password
                </RegistrationFormLabel>
                <TextBox
                  labelId="confirm-password-label"
                  value={confirmPassword}
                  handleChange={(e) => handlePswdChange(e, "confirmPassword")}
                  fullWidth
                  type="password"
                  placeholder="***************"
                  customStyles={{
                    height: "40px",
                    border: "1.5px solid var(--Neutrals-500, #E2E6E9)",
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
        {pswdErrorMsg && (
          <Typography
            sx={{
              color: "#F5325B",
              fontFamily: "Lato",
              fontSize: 12,
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              paddingLeft: "14px",
            }}>
            {pswdErrorMsg}
          </Typography>
        )}
        {pswdHelpText}
      </Grid>

      <Grid
        item
        sx={{
          width: "100%",
          height: "60px",
        }}>
        <LabelButton
          type="primary"
          variant="contained"
          text={"Complete Registration"}
          function={handleCompleteRegistration}
          disableButtonStyle={{
            border: "none",
            background: "var(--Primary-500, #16124C)",
            color: "var(--Neutrals-100, #FFF)",
            opacity: 0.25,
          }}
        />
      </Grid>
    </>
  );
}

function RegistrationFormLabel({ id, children }) {
  return (
    <Typography
      sx={{
        color: "#A4A4A4",
        fontFamily: "Lato",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        marginBottom: "10px",
      }}>
      {children}
    </Typography>
  );
}

function pswdHelpTextList(index, msg, hasError) {
  return (
    <ListItem
      key={index}
      disableGutters
      sx={{ paddingY: "1px" }} // Adjust vertical spacing between items
    >
      <ListItemIcon sx={{ minWidth: 12 }}>
        <Typography
          component="span"
          sx={{
            color: hasError ? "#F5325B" : "#2E7D32",
            fontFamily: "Lato",
            fontSize: 10,
            fontStyle: "normal",
            fontWeight: "regular",
            lineHeight: "normal",
          }}>
          •
        </Typography>
      </ListItemIcon>
      <ListItemText
        primary={msg}
        sx={{
          marginTop: 0,
          marginBottom: 0,
        }}
        primaryTypographyProps={{
          sx: {
            color: hasError ? "#F5325B" : "#2E7D32",
            fontFamily: "Lato",
            fontSize: 10,
            fontStyle: "normal",
            fontWeight: "regular",
            lineHeight: "normal",
            textTransform: "none",
          },
        }}
      />
    </ListItem>
  );
}
